import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ShoppingBag, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1484980972926-edee96e0960d?auto=format&fit=crop&w=600&q=70';

/**
 * SmartRecommendations — "Sizin için" widget; uses user history else falls back to popular.
 * Renders nothing when there is no token (anonymous visitors). Compact horizontal scroll on mobile.
 */
const SmartRecommendations = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ source: 'popular', items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/recommendations/for-me?limit=6`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setData(r.data || { items: [] }))
      .catch(() => setData({ items: [] }))
      .finally(() => setLoading(false));
  }, [token]);

  if (!token || loading || data.items.length === 0) return null;

  const isHistory = data.source === 'history';

  return (
    <section className="py-12 md:py-16 bg-bone" data-testid="smart-recommendations">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-gold" />
              {isHistory ? 'Sizin için' : 'Şu an popüler'}
            </span>
            <h2 className="font-playfair text-3xl md:text-4xl tracking-tight mt-2">
              {isHistory ? 'Geçmiş siparişlerinizden esinlendik' : 'Misafirlerimizin favorileri'}
            </h2>
          </div>
          <Link to="/alakart" className="text-sm font-semibold text-terracotta inline-flex items-center gap-1 group">
            Tüm Menü <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {data.items.map((it, i) => (
            <motion.div
              key={`${it.item_id || it.name}-${i}`}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="group relative rounded-2xl overflow-hidden ring-1 ring-charcoal/5 bg-white"
              data-testid={`smart-rec-${i}`}
            >
              <Link to="/alakart" className="block">
                <div className="aspect-square overflow-hidden bg-sand">
                  <img src={it.image || PLACEHOLDER_IMG} alt={it.name_tr} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                </div>
                <div className="p-3">
                  <p className="font-semibold text-sm text-charcoal line-clamp-1">{it.name_tr}</p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="font-playfair text-lg font-bold text-terracotta">€{it.price.toFixed(2)}</span>
                    {isHistory && it.ordered_count > 1 && (
                      <span className="text-[10px] text-charcoal/50 inline-flex items-center gap-0.5">
                        <ShoppingBag className="w-3 h-3" />×{it.ordered_count}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SmartRecommendations;
